import { z } from '@customerio/hydra-validation';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { hydraUiApi } from '@/services/api';
import type { QueryConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import { collectionKeys } from './entity';

/* ------------------------------------------------------------------------- */
/* Types                                                                      */
/* ------------------------------------------------------------------------- */

/** API-triggered broadcasts are campaigns, so they arrive in the same list under this type. @public */
export const TRIGGERED_BROADCAST_TYPE = 'triggered_broadcast';

/** @public */
export type CollectionUsageAction = {
  id: string;
  name: string;
  attribute_name: string;
  state: string;
};

/** @public */
export type CollectionUsageCampaign = {
  id: string;
  name: string;
  type: string;
  state: string;
  updated: Date | null;
  archived: boolean;
  actions: CollectionUsageAction[];
};

/**
 * An action's own name is usually empty: `campaigns.IsNameRequired` in services excludes
 * query-collection actions, so the editor never asks for one. `attribute_name` always is set.
 *
 * @public
 */
export function usageActionLabel(action: CollectionUsageAction): string {
  return action.name.length > 0 ? action.name : action.attribute_name;
}

/* ------------------------------------------------------------------------- */
/* Schema                                                                     */
/* ------------------------------------------------------------------------- */

const optionalString = z
  .string()
  .nullish()
  .transform((value) => value ?? '');

const UsageActionFromApi = z.object({
  id: z.coerce.string(),
  name: optionalString,
  attribute_name: optionalString,
  state: optionalString,
});

const UsageCampaignFromApi = z.object({
  id: z.coerce.string(),
  name: optionalString,
  type: optionalString,
  state: optionalString,
  updated: z
    .number()
    .nullish()
    .transform((seconds) => (seconds == null ? null : new Date(seconds * 1000))),
  archived: z
    .boolean()
    .nullish()
    .transform((archived) => archived ?? false),
  actions: z
    .array(UsageActionFromApi)
    .nullish()
    .transform((actions) => actions ?? []),
});

const CollectionUsageFromApi = z.object({
  usage: z
    .object({
      campaigns: z
        .array(UsageCampaignFromApi)
        .nullish()
        .transform((campaigns) => campaigns ?? []),
    })
    .nullish()
    .transform((usage) => usage ?? { campaigns: [] }),
});

/* ------------------------------------------------------------------------- */
/* Fetch                                                                      */
/* ------------------------------------------------------------------------- */

/**
 * Parsed rather than trusted: the generated types declare `campaigns`, `actions` and `updated`
 * as always present, but Go marshals a nil slice as `null`, so one sparse or timestamp-less
 * campaign would otherwise throw and blank the whole tab.
 *
 * @public
 */
export async function getCollectionUsage(
  workspaceId: number,
  id: string,
  signal?: AbortSignal,
): Promise<CollectionUsageCampaign[]> {
  const raw = await hydraUiApi.collections.getUsage({
    params: { path: { environment_id: String(workspaceId), id } },
    signal,
  });

  return CollectionUsageFromApi.parse(raw).usage.campaigns;
}

/* ------------------------------------------------------------------------- */
/* Query                                                                      */
/* ------------------------------------------------------------------------- */

/** Nested under the detail key so deleting the collection drops its usage with it. */
export const collectionUsageKey = (workspaceId: number, id: string) =>
  [...collectionKeys.detail(workspaceId, id), 'usage'] as const;

export const getCollectionUsageQueryOptions = (workspaceId: number, id: string) =>
  queryOptions({
    queryKey: collectionUsageKey(workspaceId, id),
    queryFn: ({ signal }) => getCollectionUsage(workspaceId, id, signal),
  });

/**
 * Not a suspense query, unlike its siblings: the table renders its own skeleton rows off
 * `isLoading` rather than suspending the whole page to the layout's PageLoader.
 *
 * @public
 */
export const useCollectionUsage = ({
  id,
  queryConfig,
}: {
  id: string;
  queryConfig?: QueryConfig<typeof getCollectionUsageQueryOptions>;
}) => {
  const workspaceId = requireWorkspaceId();
  return useQuery({ ...getCollectionUsageQueryOptions(workspaceId, id), ...queryConfig });
};

/* ------------------------------------------------------------------------- */
/* Batch — usage for every collection in the workspace                        */
/* ------------------------------------------------------------------------- */

/** @public */
export type CollectionUsageById = Record<string, CollectionUsageCampaign[]>;

/**
 * Alone among the collections endpoints, the batch read is unenveloped — a bare id → usage map,
 * where its single-collection sibling and the list/detail reads all wrap their payload. So
 * `campaigns` is required on each entry rather than optional: without that, any object at all
 * would parse, and an envelope appearing here later would resolve to zero campaigns per row.
 * The cell draws that as a dash meaning "unused", so the whole column would quietly under-report
 * instead of showing its error state. A null entry still means unused, which services can send.
 */
const CollectionsUsageFromApi = z.record(
  z.string(),
  z
    .object({ campaigns: z.array(UsageCampaignFromApi).nullable() })
    .nullable()
    .transform((usage) => usage?.campaigns ?? []),
);

/** One slot for the whole workspace, so a list of any length costs a single request. */
export function collectionsUsageQueryKey(workspaceId: number) {
  return [...collectionKeys.all(workspaceId), 'usage'] as const;
}

/**
 * Omits `collection_ids` deliberately: services reads that as "every collection in the
 * workspace", and sending an explicit list risks a shorter answer than the rows on screen.
 *
 * @public
 */
export async function listCollectionsUsage(workspaceId: number, signal?: AbortSignal): Promise<CollectionUsageById> {
  const raw = await hydraUiApi.collections.getCollectionsUsage({
    params: { path: { environment_id: String(workspaceId) } },
    signal,
  });

  return CollectionsUsageFromApi.parse(raw);
}

export const collectionsUsageQueryOptions = (workspaceId: number) =>
  queryOptions({
    queryKey: collectionsUsageQueryKey(workspaceId),
    queryFn: ({ signal }) => listCollectionsUsage(workspaceId, signal),
  });

// A collection nothing references is absent from the map, and the cell reads `.length`.
const NO_USAGE: CollectionUsageCampaign[] = [];

/**
 * Usage for one row, selected out of the single batch read every row shares — so a list of 200
 * collections costs one request rather than 200.
 *
 * @public
 */
export const useCollectionsUsage = (workspaceId: number, collectionId: string) =>
  useQuery({
    ...collectionsUsageQueryOptions(workspaceId),
    select: (byId: CollectionUsageById) => byId[collectionId] ?? NO_USAGE,
  });
